import { useEffect, useState } from 'react';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/store/appDispatch.ts';
import OTPCodeModal from '~/components/OTPCodeModal/OTPCodeModal.tsx';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { RemoteBankingContactsHomePage } from '~/modules/remote-banking-contacts/RemoteBankingContactsHomePage.tsx';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  otpVerificationCheckRemoteBankingContacts,
  otpVerificationStatusRemoteBankingContacts,
} from '~/store/otp-verification/thunks/otpVerificationStatusThunks.ts';
import SuccessModal from '~/components/modals/SuccessModal/SuccessModal.tsx';
import { useNavigate } from 'react-router';
import { infoMessages } from '~/constants/infoMessages.ts';
import { statusVerifyContrAgent } from '~/store/remote-banking-contacts/thunks/verifyContragentThunks.ts';
import { setRemoteBankingContactsError } from '~/store/remote-banking-contacts/slices.ts';
import { ClientRole } from '~/constants/clientTypes';
import { setUserByUrlToken } from '~/store/users/slices';
import { RootState } from '~/store/store.ts';
import { TClientUser } from '~/store/users/types.ts';
import { SubmitApplicationStatus } from '~/modules/remote-banking-contacts/constants.tsx';

const RemoteBankingContactsAuthentication = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { loading: isLoadingRemoteBankingContacts, error: errorRemoteBankingContacts } =
    useSelector((state: RootState) => state.remoteBankingContacts) || {};
  const { token, loading, error: userError } = useSelector((state: RootState) => state.users) || {};
  const { tokenData, roles } =
    useSelector((state: RootState) => state.users.user as TClientUser) || {};
  const { applicationId, phoneNumber, personId } = tokenData || {};
  const isLoading = loading || isLoadingRemoteBankingContacts;
  const error = userError || errorRemoteBankingContacts;

  const [otpError, setOtpError] = useState<string>('');
  const [modalOTPVerification, setModalOTPVerification] = useState<boolean>(false);
  const [isOpenMainForm, setIsOpenMainForm] = useState<boolean>(false);
  const [verificationError, setVerificationError] = useState<string>('');
  const [isOpenModalCongratulations, setIsOpenModalCongratulations] = useState<boolean>(false);
  const [isModalCongratulationsText, setIsModalCongratulationsText] = useState<string | null>(null);

  useEffect(() => {
    dispatch(setUserByUrlToken());
  }, [dispatch]);

  useEffect(() => {
    if (applicationId) {
      setModalOTPVerification(true);
    }
  }, [applicationId]);

  const closeAlertModal = () => {
    errorRemoteBankingContacts && dispatch(setRemoteBankingContactsError(''));
    setVerificationError('');
    navigate('/services');
  };

  const closeModalCongratulations = () => {
    setIsOpenModalCongratulations(false);
    navigate('/');
  };

  const getFormApplication = async () => {
    try {
      if (isLoading) return;

      try {
        await dispatch(
          otpVerificationStatusRemoteBankingContacts({
            applicationId: applicationId!,
            verificationStatus: SubmitApplicationStatus.VERIFIED,
            personId: personId!,
            token,
          })
        ).unwrap();

        const isDirector = roles?.includes(ClientRole.ROLE_REMOTE_BANKING_DIRECTOR) ?? false;

        if (isDirector) {
          const { status } = await dispatch(
            statusVerifyContrAgent({ applicationId: applicationId!, token })
          ).unwrap();

          if (status === SubmitApplicationStatus.NEW) {
            setModalOTPVerification(false);
            setIsOpenMainForm(true);
          } else {
            dispatch(setRemoteBankingContactsError(errorMessages.APPLICATION_IS_EXPIRED));
          }
        } else {
          await dispatch(
            otpVerificationCheckRemoteBankingContacts({
              applicationId: applicationId!,
              token,
            })
          );

          setIsOpenModalCongratulations(true);
          setIsModalCongratulationsText(infoMessages.DATA_SUCCESSFULLY_UPDATED);
        }
      } catch {
        setModalOTPVerification(true);
        setVerificationError(errorMessages.SERVICE_UNAVAILABLE);
      }
    } catch {
      setIsOpenMainForm(false);
    }
  };

  const handleValidationSuccess = async () => {
    await getFormApplication();
  };

  return (
    <>
      <OTPCodeModal
        modalOTPVerification={modalOTPVerification}
        phone={phoneNumber!}
        error={otpError}
        token={token}
        setError={setOtpError}
        onValidationSuccess={handleValidationSuccess}
      />

      <RemoteBankingContactsHomePage isOpen={isOpenMainForm} />

      {isOpenModalCongratulations && (
        <SuccessModal
          isOpen={isOpenModalCongratulations}
          text={isModalCongratulationsText}
          onClose={closeModalCongratulations}
          showButton={true}
          onClick={closeModalCongratulations}
          buttonValue="Завершити"
          dataTestId="modalSuccess"
        />
      )}
      {isLoading && <FullPageLoader />}
      {!!error && <ModalAlert isOpen={!!error} text={error} onClose={closeAlertModal} />}
      {!!verificationError && (
        <ModalAlert
          isOpen={!!verificationError}
          text={verificationError}
          onClose={closeAlertModal}
        />
      )}
    </>
  );
};

export default RemoteBankingContactsAuthentication;
